import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import type { EvacuationPoint, EvacuationView, RollCallPerson, RollCallStatus } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useSite } from '../../lib/site';
import { useLiveChannel } from '../../lib/realtime';
import { Alert, Badge, Card, ConfirmButton, PageHeader, SelectField, Spinner } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { LocationScatter, RollCallList } from '../../components/phase3';
import type { ScatterPoint } from '../../components/phase3';
import { BuildingTotalsBanner } from '../shared-reception/parts';
import { activeKey, applyLive, useActive, useEndEvacuation, useMarkPerson, useStartEvacuation } from './api';

const SOURCES = ['portal', 'kiosk', 'companion', 'fire_panel', 'system', 'building'] as const;

/** "Safe at Car park, by the app, 10:03" lines for people whose marks disagree (ADR 0021 rule 3). */
export function CheckDetails({ people, points, zone }: { people: readonly RollCallPerson[]; points: readonly EvacuationPoint[]; zone?: string }) {
  const rows = people.filter((p) => p.check && (p.checkMarks?.length ?? 0) > 0);
  if (rows.length === 0) return null;
  const pointName = (id: string | null) => points.find((p) => p.id === id)?.name ?? '';
  return (
    <details className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
      <summary className="cursor-pointer font-medium text-red-900">
        {t('evac.checkDetail')} ({rows.length})
      </summary>
      <ul className="mt-2 space-y-2">
        {rows.map((p) => (
          <li key={p.id}>
            <p className="font-semibold">{p.name}</p>
            <ul className="ml-4 list-disc text-slate-800">
              {(p.checkMarks ?? []).map((m, i) => {
                const params = { status: t(`evac.status.${m.status}`), by: m.byLabel, time: formatDateTime(m.at, zone), point: pointName(m.pointId) };
                return <li key={i}>{m.pointId && params.point ? t('evac.checkMarkPoint', params) : t('evac.checkMark', params)}</li>;
              })}
            </ul>
          </li>
        ))}
      </ul>
    </details>
  );
}

function scatterOf(view: EvacuationView): { centre: { lat: number; lng: number; name: string } | null; points: ScatterPoint[] } {
  const muster = view.points.filter((p) => p.lat !== null && p.lng !== null);
  const people: ScatterPoint[] = view.people
    .filter((p) => p.selfLocation)
    .map((p) => ({ id: p.id, name: t('evac.locations.person', { name: p.name }), lat: p.selfLocation!.lat, lng: p.selfLocation!.lng, accuracyM: p.selfLocation!.accuracyM, kind: 'person' as const }));
  const first = muster[0];
  const centre = first
    ? { lat: first.lat as number, lng: first.lng as number, name: t('evac.locations.centre') }
    : people[0]
      ? { lat: people[0].lat, lng: people[0].lng, name: t('evac.locations.centre') }
      : null;
  const musterPoints: ScatterPoint[] = muster.map((p) => ({ id: p.id, name: p.name, lat: p.lat as number, lng: p.lng as number, kind: 'muster' as const }));
  return { centre, points: [...musterPoints, ...people] };
}

/** The Evacuate page: start, the live roll call, and end. */
export function EvacuatePage() {
  const qc = useQueryClient();
  const { sites, site, siteId, setSiteId, loading } = useSite();
  const canStart = useCan('evacuation.start');
  const canMark = useCan('evacuation.mark');
  const usable = sites.filter((s) => !s.isRemote);
  const remote = Boolean(site?.isRemote);
  const activeSiteId = remote ? null : siteId;

  const q = useActive(activeSiteId);
  const start = useStartEvacuation(activeSiteId);
  const end = useEndEvacuation(activeSiteId);
  const mark = useMarkPerson(activeSiteId);
  const [endedId, setEndedId] = useState<string | null>(null);
  const [already, setAlready] = useState(false);
  const lastId = useRef<string | null>(null);

  const refetch = () => void qc.invalidateQueries({ queryKey: activeKey(activeSiteId) });
  const { connected } = useLiveChannel(
    'evacuation',
    activeSiteId,
    (data) => {
      const next = applyLive(qc.getQueryData<EvacuationView | null>(activeKey(activeSiteId)), data);
      if (next === 'refetch' || next === null) refetch();
      else qc.setQueryData(activeKey(activeSiteId), next);
    },
    refetch,
  );

  const view = q.data ?? null;
  useEffect(() => {
    if (view) {
      lastId.current = view.evacuation.id;
      setEndedId(null);
    } else if (q.isSuccess && lastId.current) {
      setEndedId(lastId.current);
      lastId.current = null;
    }
  }, [view, q.isSuccess]);
  useEffect(() => {
    lastId.current = null;
    setEndedId(null);
    setAlready(false);
  }, [activeSiteId]);

  const scatter = useMemo(() => (view ? scatterOf(view) : null), [view]);
  const mutationError = mark.error ?? start.error ?? end.error;

  const picker =
    usable.length > 1 ? (
      <SelectField label={t('evac.site')} value={siteId ?? ''} onChange={(e) => setSiteId(e.target.value)} className="max-w-xs">
        {usable.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </SelectField>
    ) : null;

  let body;
  if (loading) body = <Spinner />;
  else if (!siteId || !site) body = <Alert kind="info">{t('evac.noSite')}</Alert>;
  else if (remote) body = <Alert kind="info">{t('evac.remoteSite')}</Alert>;
  else if (q.isError) body = <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  else if (q.isLoading) body = <Spinner />;
  else if (!view) {
    body = (
      <div className="space-y-4">
        {endedId ? (
          <Alert kind="success">
            <span className="font-medium">{t('evac.ended.title')}</span> {t('evac.ended.body')}{' '}
            <Link className="font-medium underline" to={`/reports/evacuations/${endedId}`}>
              {t('evac.ended.open')}
            </Link>
          </Alert>
        ) : null}
        <Card title={t('evac.none.title', { site: site.name })} description={t('evac.none.body')}>
          {canStart ? (
            <div className="flex flex-wrap gap-3">
              <ConfirmButton
                label={t('evac.start.real')}
                confirmLabel={t('evac.start.realConfirm')}
                pending={start.isPending}
                onConfirm={() => start.mutate('real', { onSuccess: (r) => setAlready(r.alreadyActive) })}
              />
              <ConfirmButton
                label={t('evac.start.drill')}
                confirmLabel={t('evac.start.drillConfirm')}
                pending={start.isPending}
                onConfirm={() => start.mutate('drill', { onSuccess: (r) => setAlready(r.alreadyActive) })}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-600">{t('evac.start.noPermission')}</p>
          )}
        </Card>
      </div>
    );
  } else {
    const ev = view.evacuation;
    const real = ev.kind === 'real';
    body = (
      <div className="space-y-4">
        {already ? <Alert kind="info">{t('evac.start.already')}</Alert> : null}
        <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${real ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
          <div>
            <p className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <TriangleAlert aria-hidden="true" className={`h-5 w-5 ${real ? 'text-red-700' : 'text-amber-700'}`} />
              {real ? t('evac.banner.real') : t('evac.banner.drill')}
              <Badge tone={real ? 'bad' : 'warn'}>{t(`evac.kind.${ev.kind}`)}</Badge>
            </p>
            <p className="mt-1 text-sm text-slate-700">
              {t('evac.banner.detail', {
                time: formatDateTime(ev.startedAt, site.timeZone),
                by: ev.startedByLabel,
                source: t(`evac.source.${SOURCES.includes(ev.startedSource) ? ev.startedSource : 'system'}`),
              })}
            </p>
          </div>
          {canStart ? (
            <div className="space-y-1">
              <ConfirmButton label={t('evac.end')} confirmLabel={t('evac.end.confirm')} pending={end.isPending} onConfirm={() => end.mutate(ev.id)} />
              {ev.counts.unaccounted > 0 ? <p className="text-xs text-slate-700">{t('evac.end.unaccounted', { count: ev.counts.unaccounted })}</p> : null}
            </div>
          ) : null}
        </div>
        <CheckDetails people={view.people} points={view.points} zone={site.timeZone} />
        <RollCallList
          people={view.people}
          points={view.points}
          live={connected}
          readOnly={!canMark}
          onMark={(person: RollCallPerson, status: RollCallStatus) => mark.mutate({ evacuationId: ev.id, person, status, pointId: person.pointId })}
          onChoosePoint={(person: RollCallPerson, pointId: string | null) => mark.mutate({ evacuationId: ev.id, person, status: person.status, pointId })}
        />
        {scatter?.centre && scatter.points.some((p) => p.kind === 'person') ? (
          <Card title={t('evac.locations.title')} description={t('evac.locations.description')}>
            <LocationScatter title={t('evac.locations.title')} center={scatter.centre} points={scatter.points} />
          </Card>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t('evac.page.title')} description={t('evac.page.description')} actions={picker} />
      <ErrorLine error={mutationError} />
      <BuildingTotalsBanner siteId={activeSiteId} />
      <div className="mt-4">{body}</div>
    </div>
  );
}
